/**
 * A failed request, shown to a person.
 *
 * The backend's messages are written for a developer — "resource_not_found",
 * "This action requires the \"user.create\" permission." — so the ones a user
 * can actually act on are translated here, and the rest are shown verbatim
 * rather than replaced with something vague. A wrong-but-friendly message is
 * worse than a blunt accurate one; it sends people to the wrong place.
 *
 * **The backend is not localised**, so `error.message` arrives in English in
 * every language. That is why the headline is keyed off the stable `code`
 * instead: the part a user can act on is translated, and the developer detail
 * underneath stays in the language the server wrote it in rather than being
 * dropped. Widening this means localising the API — see `i18n/README.md`.
 */
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ApiError } from '../api/client';

export interface ErrorStateProps {
  error: Error;
  /** Draws a retry button. Omit where retrying cannot help.  */
  onRetry?: () => void;
  /** `error` for a failure that stopped the screen, `warning` for one beside a form. */
  severity?: 'error' | 'warning';
}

/**
 * The key for the headline, chosen from the backend's stable error code.
 *
 * Returns a key rather than a string so the mapping stays a pure function of
 * the code — the translation happens once, at the call site that has `t`. The
 * union return type is what keeps `t()` type-checked; widening it to `string`
 * turns every key here back into an unverified literal.
 */
type ErrorTitleKey =
  | 'errors:title.unknown'
  | 'errors:title.forbidden'
  | 'errors:title.notFound'
  | 'errors:title.notAuthenticated'
  | 'errors:title.networkError'
  | 'errors:title.serverFailed'
  | 'errors:title.requestFailed';

function titleKeyFor(error: Error): ErrorTitleKey {
  if (!(error instanceof ApiError)) return 'errors:title.unknown';

  switch (error.code) {
    case 'forbidden':
      return 'errors:title.forbidden';
    case 'not_found':
    case 'resource_not_found':
      return 'errors:title.notFound';
    case 'not_authenticated':
      return 'errors:title.notAuthenticated';
    case 'network_error':
      return 'errors:title.networkError';
    default:
      return error.status >= 500 ? 'errors:title.serverFailed' : 'errors:title.requestFailed';
  }
}

export function ErrorState({ error, onRetry, severity = 'error' }: ErrorStateProps) {
  const { t } = useTranslation(['errors', 'common']);
  const isApi = error instanceof ApiError;

  return (
    <Alert
      severity={severity}
      sx={{ my: 2 }}
      {...(onRetry
        ? {
            action: (
              <Button color="inherit" size="small" onClick={onRetry}>
                {t('common:action.retry')}
              </Button>
            ),
          }
        : {})}
    >
      <AlertTitle>{t(titleKeyFor(error))}</AlertTitle>
      {error.message}
      {isApi && (
        // The stable code, so a bug report names something greppable rather
        // than a sentence that may be reworded.
        <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
          {error.code}
          {error.status ? ` · ${t('errors:httpStatus', { status: error.status })}` : ''}
        </Typography>
      )}
    </Alert>
  );
}
